// The project's sales enablement deck (v1, Edward 2026-10-23) — the "Sales
// deck" step on the project rail, after Page build. Three moves:
// 1. the PM confirms the deck facts (prefilled from NeTrA and the strategy,
//    each with where it came from; the work order's numbers are typed);
// 2. Build — one writing pass for the descriptive copy only, then the
//    backend's quick checks (schema, brand spelling, other clients' names…);
// 3. Download the .pptx, drawn here in the browser (utils/deck/renderDeck.js),
//    proof it in PowerPoint, and mark it proofed / sent.
// Numbers and the questions leads answered are never written by the model —
// they come from these facts exactly (playbook:
// backend core_docs/sales-enablement-deck-playbook.md).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { fmtDate } from '../../components/ZbSummary';
import { saveBlob } from '../../utils/ciqAssets';
import {
  FONTS, HUES, INK, NEUTRAL, RADII, STATE,
  buttonDisabled, buttonLink, buttonPrimary, buttonSmall, card, displayFont, pill,
} from '../../utils/wizardTheme';

const API = process.env.REACT_APP_API_URL;
const HUE = HUES.scripts;

export const DECK_STATUS = {
  draft: { label: 'Draft', fg: INK.secondary, bg: NEUTRAL.chipBg },
  pm_proofed: { label: 'Proofed by the PM', fg: HUE.deep, bg: HUE.tint },
  sent: { label: 'Sent to the client', fg: STATE.done.text, bg: STATE.done.tint },
};

// ── the rail step ───────────────────────────────────────────────────────
// The wizard's phase-4 list gets one more row; its state comes from the deck.
export function deckRailStep(deck) {
  const base = { key: 'deck', label: 'Sales deck', done: false };
  if (!deck) return base;
  if (!deck.eligibility?.has_deck) return { ...base, dimmed: true, badge: { label: 'no deck', tone: 'muted' } };
  if (deck.status === 'sent') return { ...base, done: true };
  if (deck.status === 'pm_proofed') return { ...base, badge: { label: 'proofed', tone: 'done' } };
  if (deck.config) {
    return { ...base, badge: deck.stale ? { label: 'facts changed', tone: 'gate' } : { label: 'draft', tone: 'muted' } };
  }
  return base;
}

// The row exactly as CiqPhaseRail draws it.
export function deckRailRow(step, isCurrent, clickable) {
  return { ...step, state: isCurrent ? 'current' : step.done ? 'done' : 'todo', clickable };
}

export function useDeckRailStep(projectId) {
  const [deck, setDeck] = useState(null);
  const refresh = useCallback(() => {
    if (!projectId) return;
    axios.get(`${API}/contentiq/projects/${projectId}/deck`).then(r => setDeck(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { refresh(); }, [refresh]);
  return { step: deckRailStep(deck), refresh };
}

// ── the facts form ──────────────────────────────────────────────────────

const PROGRAM_FIELDS = [
  ['client', 'Client'], ['program', 'Program'], ['geography', 'Geography'], ['leads', 'Leads'],
  ['target_accounts', 'Target accounts'], ['company_size', 'Company size'],
  ['title_level', 'Title level'], ['leads_per_account', 'Leads per account'],
];

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontSize: '13px',
  fontFamily: FONTS.body, color: INK.primary, background: NEUTRAL.pageBg,
  border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small,
};
const hintStyle = { fontSize: '11px', color: INK.muted, lineHeight: 1.4 };
const sectionLabel = {
  fontSize: '11px', fontWeight: 700, color: INK.secondary, letterSpacing: '0.6px', textTransform: 'uppercase',
};

function Field({ label, hint, value, onChange, rows, placeholder }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px', color: INK.body, minWidth: 0 }}>
      {label}
      {rows ? (
        <textarea rows={rows} value={value ?? ''} placeholder={placeholder}
          onChange={e => onChange(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
      ) : (
        <input value={value ?? ''} placeholder={placeholder}
          onChange={e => onChange(e.target.value)} style={inputStyle} />
      )}
      {hint && <span style={hintStyle}>{hint}</span>}
    </label>
  );
}

function Grid({ children, cols = 2 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '10px 14px' }}>
      {children}
    </div>
  );
}

function FactsForm({ facts, sources, product, onChange }) {
  const set = (key, v) => onChange({ ...facts, [key]: v });
  const setIn = (key, sub, v) => onChange({ ...facts, [key]: { ...(facts[key] || {}), [sub]: v } });
  const personas = facts.personas || [];
  const setPersona = (i, k, v) => set('personas', personas.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const routing = facts.routing;
  const setPath = (i, k, v) => set('routing', {
    ...routing, paths: routing.paths.map((p, j) => (j === i ? { ...p, [k]: v } : p)),
  });
  const need = facts.need;
  const setGroup = (i, k, v) => set('need', {
    ...need, groups: need.groups.map((g, j) => (j === i ? { ...g, [k]: v } : g)),
  });
  const sc = facts.stage_counts || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={sectionLabel}>The program — from the work order</div>
        <Grid>
          {PROGRAM_FIELDS.map(([key, label]) => (
            <Field key={key} label={label} value={facts[key]} hint={sources[key]}
              onChange={v => set(key, v)} />
          ))}
        </Grid>
      </div>

      {product === 'Demand Cascade' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={sectionLabel}>The funnel — leads by the stage they reached</div>
          <Grid cols={3}>
            <Field label="Stopped at Stage 1" value={sc.s1} onChange={v => setIn('stage_counts', 's1', v)} />
            <Field label="Stopped at Stage 2" value={sc.s2} onChange={v => setIn('stage_counts', 's2', v)} />
            <Field label="Stage 3 — the sales leads" value={sc.s3} onChange={v => setIn('stage_counts', 's3', v)} />
          </Grid>
          <span style={hintStyle}>
            {sources.stage_counts} The slide shows how many leads started each stage, and only Stage 3 is
            called “call-confirmed” for sales.
          </span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={sectionLabel}>Who the leads are</div>
        {sources.personas && <span style={hintStyle}>{sources.personas}</span>}
        {personas.map((p, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: '8px', alignItems: 'end' }}>
            <Field label={i === 0 ? 'Persona group' : ''} value={p.h} onChange={v => setPersona(i, 'h', v)} />
            <Field label={i === 0 ? 'Subtitle (optional)' : ''} value={p.sub} onChange={v => setPersona(i, 'sub', v)} />
            <Field label={i === 0 ? 'Titles' : ''} value={p.d} onChange={v => setPersona(i, 'd', v)} />
            <button onClick={() => set('personas', personas.filter((_, j) => j !== i))}
              title="Remove this persona group" style={{ ...buttonSmall, marginBottom: '1px' }}>✕</button>
          </div>
        ))}
        {personas.length < 3 && (
          <button onClick={() => set('personas', [...personas, { h: '', sub: '', d: '' }])}
            style={{ ...buttonLink, alignSelf: 'flex-start' }}>+ Add a persona group</button>
        )}
        <Grid>
          <Field label="Priority industries" value={facts.industries?.priority}
            onChange={v => setIn('industries', 'priority', v)} />
          <Field label="Also in scope" value={facts.industries?.also}
            onChange={v => setIn('industries', 'also', v)} />
          <Field label={`How to introduce ${facts.client || 'the client'} — heading (optional)`}
            value={facts.brand_intro?.heading} onChange={v => setIn('brand_intro', 'heading', v)}
            hint="When filled in, this card replaces the industries card on the audience slide." />
          <Field label="… and the text" rows={2} value={facts.brand_intro?.text}
            onChange={v => setIn('brand_intro', 'text', v)} />
        </Grid>
      </div>

      {routing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={sectionLabel}>The routing question — verbatim</div>
          <span style={hintStyle}>{sources.routing}. Change it only to match the live page.</span>
          <Field label="Question" value={routing.question} onChange={v => set('routing', { ...routing, question: v })} />
          {(routing.paths || []).map((p, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1fr', gap: '8px' }}>
              <Field label={i === 0 ? 'Choice' : ''} value={p.choice} onChange={v => setPath(i, 'choice', v)} />
              <Field label={i === 0 ? 'What they read' : ''} value={p.answer} onChange={v => setPath(i, 'answer', v)} />
              <Field label={i === 0 ? 'Asset they got' : ''} value={p.asset} onChange={v => setPath(i, 'asset', v)} />
              <Field label={i === 0 ? 'Kind' : ''} value={p.kind} onChange={v => setPath(i, 'kind', v)} />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={sectionLabel}>The need question — verbatim</div>
        <span style={hintStyle}>{sources.need}</span>
        {need ? (
          <>
            <Field label="Question" value={need.question} onChange={v => set('need', { ...need, question: v })} />
            {(need.groups || []).map((g, i) => (
              <Grid key={i}>
                <Field label={i === 0 ? 'Follow-up label (optional)' : 'Second question'} value={g.label}
                  onChange={v => setGroup(i, 'label', v)} />
                <Field label="Answer options, one per line" rows={3} value={(g.options || []).join('\n')}
                  onChange={v => setGroup(i, 'options', v.split('\n'))} />
              </Grid>
            ))}
          </>
        ) : (
          <button onClick={() => set('need', { question: '', groups: [{ label: '', options: [] }], client_guidance: false })}
            style={{ ...buttonLink, alignSelf: 'flex-start' }}>+ Add the need question</button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={sectionLabel}>Contact and link</div>
        <Grid>
          <Field label="PM name (FAQ slide)" value={facts.pm_name} hint={sources.pm_name} onChange={v => set('pm_name', v)} />
          <Field label="PM email" value={facts.pm_email} onChange={v => set('pm_email', v)} />
          {product !== 'Demand Cascade' && (
            <Field label="Live page link (campaign slide)" value={facts.live_url} hint={sources.live_url}
              onChange={v => set('live_url', v)} />
          )}
        </Grid>
      </div>
    </div>
  );
}

// ── checks and outline ──────────────────────────────────────────────────

// The facts that become the audience slide's numbers (backend
// sales_deck._audience_slide); it needs at least three.
const AUDIENCE_KEYS = ['leads', 'target_accounts', 'geography', 'company_size', 'title_level', 'leads_per_account'];

const CHECK_LOOK = {
  pass: { mark: '✓', color: STATE.done.text },
  warn: { mark: '!', color: STATE.gate.text },
  fail: { mark: '✗', color: STATE.danger.text },
};

function Checks({ checks }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {checks.map((c, i) => {
        const look = CHECK_LOOK[c.status] || CHECK_LOOK.warn;
        return (
          <div key={`${c.id}-${i}`} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '12.5px' }}>
            <span style={{ color: look.color, fontWeight: 700, width: '12px', flexShrink: 0 }}>{look.mark}</span>
            <span style={{ color: INK.body }}>{c.label}</span>
            {c.status !== 'pass' && c.detail && (
              // The format check's own detail is a settings path; say what to do instead.
              c.id === 'schema'
                ? <span title={c.detail} style={{ color: look.color, fontSize: '11.5px' }}>a fact the deck needs is missing or malformed — see the facts line below</span>
                : <span style={{ color: look.color, fontSize: '11.5px' }}>{c.detail}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const SLIDE_NAMES = {
  title: 'Title', who: 'Who DemandArc is', process: 'How leads were verified', paths: 'The routing paths',
  funnel: 'The funnel', audience: 'Who the leads are', script: 'How to follow up', faqs: 'Follow-up FAQs',
  campaign: 'What prospects saw',
};

export function slideOutline(config) {
  return (config?.slides || []).map((s, i) => {
    let detail = s.type === 'title' ? s.kicker : s.title || '';
    if (s.type === 'campaign') {
      const slots = s.layout === 'router' ? [s.main, ...(s.branches || [])] : (s.pages || []);
      const pending = slots.filter(x => !x?.img).length;
      detail = pending ? `${slots.length - pending} of ${slots.length} screenshots ready — the rest show “Screenshot pending”`
        : `${slots.length} screenshot${slots.length === 1 ? '' : 's'} from the page checks`;
    }
    return { n: i + 1, name: SLIDE_NAMES[s.type] || s.type, detail };
  });
}

// ── the panel ───────────────────────────────────────────────────────────

export default function SalesDeckPanel({ projectId, onChanged }) {
  const [deck, setDeck] = useState(null);
  const [facts, setFacts] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');  // '' | 'facts' | 'build' | 'render' | 'status'
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const apply = useCallback((data) => {
    setDeck(data);
    setFacts(data.facts);
    setDirty(false);
  }, []);

  useEffect(() => {
    setDeck(null); setErr(''); setNote('');
    axios.get(`${API}/contentiq/projects/${projectId}/deck`)
      .then(r => apply(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Could not load the deck.'));
  }, [projectId, apply]);

  const call = async (kind, fn) => {
    setBusy(kind); setErr(''); setNote('');
    try {
      const r = await fn();
      apply(r.data);
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.response?.data?.detail || 'That didn’t work — try again.');
    } finally {
      setBusy('');
    }
  };
  const confirmFacts = () => call('facts', () =>
    axios.put(`${API}/contentiq/projects/${projectId}/deck/facts`, { facts }));
  const build = () => call('build', () => axios.post(`${API}/contentiq/projects/${projectId}/deck/build`));
  const setStatus = (status) => call('status', () =>
    axios.put(`${API}/contentiq/projects/${projectId}/deck/status`, { status }));

  const download = async () => {
    setBusy('render'); setErr(''); setNote('');
    try {
      const { renderDeck } = await import('../../utils/deck/renderDeck');
      const { blob, pending } = await renderDeck(deck.config);
      saveBlob(blob, deck.config.output);
      setNote(pending
        ? `Downloaded. ${pending} screenshot${pending === 1 ? ' is' : 's are'} still pending — the slide shows a placeholder box to replace in PowerPoint.`
        : 'Downloaded.');
    } catch (e) {
      console.error('Sales deck render failed', e);  // the cause, for whoever debugs it
      setErr('Could not draw the deck in this browser.');
    } finally {
      setBusy('');
    }
  };

  if (!deck) {
    return err
      ? <div role="alert" style={{ color: STATE.danger.text, fontSize: '12.5px' }}>{err}</div>
      : <div style={{ fontSize: '13px', color: INK.muted }}>Loading the deck…</div>;
  }

  const elig = deck.eligibility || {};
  const status = DECK_STATUS[deck.status] || DECK_STATUS.draft;
  const checks = deck.checks || [];
  const schemaFailed = checks.some(c => c.id === 'schema' && c.status === 'fail');
  // A blank work-order number is the usual reason the format check fails:
  // the audience slide needs at least three (staging pass, 2026-10-23).
  const audienceFilled = AUDIENCE_KEYS.filter(k => String((facts || {})[k] || '').trim()).length;
  const failing = checks.filter(c => c.status === 'fail').length;
  const confirmed = !!deck.facts_confirmed_at;

  const header = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ ...displayFont('22px', 700), letterSpacing: '-0.2px' }}>Sales enablement deck</div>
        {elig.has_deck && <span style={pill(HUE.deep, HUE.tint)}>{elig.product} · {elig.slides} slides</span>}
        {elig.has_deck && deck.config && <span style={pill(status.fg, status.bg)}>{status.label}</span>}
      </div>
      <div style={{ fontSize: '12.5px', color: INK.secondary, lineHeight: 1.55, maxWidth: '760px' }}>
        A follow-up guide for {deck.facts?.client || 'the client'}’s sales team: how the leads were qualified,
        who they are and how to follow up. Numbers and the questions leads answered come from the facts below,
        exactly — only the descriptive copy is written for you.
      </div>
    </div>
  );

  if (!elig.has_deck) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontFamily: FONTS.body }}>
        {header}
        <div style={{ ...card, padding: '16px 20px', maxWidth: '760px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px' }}>No sales deck for this product</div>
          <div style={{ fontSize: '12.5px', color: INK.body, lineHeight: 1.55 }}>{elig.reason}</div>
        </div>
      </div>
    );
  }

  const stepCard = (n, title, done, children) => (
    <div style={{ ...card, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '24px', height: '24px', borderRadius: RADII.pill, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
          background: done ? STATE.done.main : HUE.main, color: NEUTRAL.pageBg,
        }}>{done ? '✓' : n}</div>
        <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: '16px' }}>{title}</div>
      </div>
      {children}
    </div>
  );
  const primary = (label, onClick, disabled) => (
    <button onClick={onClick} disabled={disabled}
      style={{ ...buttonPrimary(HUE.main), ...(disabled ? buttonDisabled : {}), alignSelf: 'flex-start' }}>
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontFamily: FONTS.body, maxWidth: '860px' }}>
      {header}

      {stepCard(1, 'Confirm the deck facts', confirmed && !dirty, (
        <>
          <FactsForm facts={facts || {}} sources={deck.sources || {}} product={elig.product}
            onChange={f => { setFacts(f); setDirty(true); }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {primary(busy === 'facts' ? 'Saving…' : confirmed ? 'Confirm the changes' : 'Confirm the facts',
              confirmFacts, !!busy || (confirmed && !dirty))}
            <span style={hintStyle}>
              {dirty ? 'Unsaved changes — confirm them to use them in the deck.'
                : confirmed ? `Confirmed by ${deck.facts_confirmed_by || 'a PM'} · ${fmtDate(deck.facts_confirmed_at)}`
                : 'Check every field against the work order — blanks are things only it knows.'}
            </span>
          </div>
          {audienceFilled < 3 && (
            <div style={{ fontSize: '12.5px', color: STATE.gate.text, background: STATE.gate.bg, borderRadius: RADII.small, padding: '8px 12px' }}>
              The audience slide needs at least 3 of: leads, target accounts, geography, company size,
              title level, leads per account — {audienceFilled} filled in. Add them from the work order.
            </div>
          )}
        </>
      ))}

      {stepCard(2, 'Build the deck', !!deck.config && !deck.stale, (
        <>
          {deck.stale && (
            <div style={{ fontSize: '12.5px', color: STATE.gate.text, background: STATE.gate.bg, borderRadius: RADII.small, padding: '8px 12px' }}>
              The facts changed after this deck was built — build it again so the deck says what they say.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {primary(busy === 'build' ? 'Building…' : deck.config ? 'Build it again' : 'Build the deck',
              build, !!busy || !confirmed || dirty)}
            <span style={hintStyle}>
              {busy === 'build' ? 'Writing the descriptive copy and checking the deck — this takes up to a minute.'
                : !confirmed || dirty ? 'Confirm the facts first.'
                : deck.generated_at ? `Built by ${deck.generated_by || 'a PM'} · ${fmtDate(deck.generated_at)}` : ''}
            </span>
          </div>
          {checks.length > 0 && <Checks checks={checks} />}
          {deck.config && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', background: NEUTRAL.band, borderRadius: RADII.small, padding: '10px 14px' }}>
              {slideOutline(deck.config).map(s => (
                <div key={s.n} style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                  <span style={{ color: INK.muted, width: '16px', textAlign: 'right', flexShrink: 0 }}>{s.n}</span>
                  <span style={{ color: INK.primary, fontWeight: 600, width: '170px', flexShrink: 0 }}>{s.name}</span>
                  <span style={{ color: INK.secondary }}>{s.detail}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ))}

      {stepCard(3, 'Download, proof and send', deck.status === 'sent', (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {primary(busy === 'render' ? 'Drawing the slides…' : 'Download .pptx', download,
              !!busy || !deck.config || schemaFailed)}
            <span style={hintStyle}>
              {!deck.config ? 'Build the deck first.'
                : schemaFailed && audienceFilled < 3 ? 'Add at least 3 audience numbers in step 1, confirm, then build again.'
                : schemaFailed ? 'The deck’s settings failed the format check — check the facts in step 1, then build again.'
                : failing ? `${failing} check${failing === 1 ? '' : 's'} failed — fix ${failing === 1 ? 'it' : 'them'} in PowerPoint, or correct the facts and build again.`
                : deck.config.output}
            </span>
          </div>
          {note && <div style={{ fontSize: '12.5px', color: STATE.done.text }}>{note}</div>}
          {deck.config && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(DECK_STATUS).map(([key, s]) => (
                  <button key={key} onClick={() => setStatus(key)} disabled={!!busy || deck.status === key}
                    style={deck.status === key
                      ? { ...buttonSmall, background: s.bg, color: s.fg, borderColor: s.bg, cursor: 'default' }
                      : buttonSmall}>
                    {s.label}
                  </button>
                ))}
              </div>
              <span style={hintStyle}>
                Proof the downloaded deck in PowerPoint — hand edits are expected — then mark it proofed, and
                sent once it’s with the client.
                {deck.status_at ? ` ${DECK_STATUS[deck.status]?.label} · ${deck.status_by || 'a PM'} · ${fmtDate(deck.status_at)}` : ''}
              </span>
            </div>
          )}
        </>
      ))}

      {err && <div role="alert" style={{ color: STATE.danger.text, fontSize: '12.5px' }}>{err}</div>}
    </div>
  );
}
